/**
 * テーブルブロック ID 安定性検証デバッガー
 *
 * dev/sandbox 環境でのみ使用。テーブルの rowId / cellId が
 * 保存・リロード・コラボ編集を通じて安定しているか検証する。
 *
 * コンソール API:
 *   __TABLE_ID_DEBUG__.capture('label')      - スナップショット取得
 *   __TABLE_ID_DEBUG__.compare()             - 直近2つを比較
 *   __TABLE_ID_DEBUG__.compareWithInitial()  - 初期ロードと現在を比較
 *   __TABLE_ID_DEBUG__.history()             - スナップショット履歴一覧
 */

import { logger } from '@/lib/logger';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** 1 セルの ID 情報 */
export type CellIdEntry = {
  position: string; // "blockIdx:rowIdx:cellIdx"
  cellId: string | undefined;
};

/** 1 行の ID 情報 */
export type RowIdEntry = {
  position: string; // "blockIdx:rowIdx"
  rowId: string | undefined;
  cells: CellIdEntry[];
};

/** テーブル 1 つ分の ID スナップショット */
export type TableSnapshot = {
  blockId: string | undefined;
  blockIndex: number;
  rows: RowIdEntry[];
};

/** 全テーブルのスナップショット */
export type TableIdSnapshot = {
  label: string;
  timestamp: number;
  tables: TableSnapshot[];
};

/** 差分結果 */
export type IdDiffEntry = {
  position: string;
  kind: 'row' | 'cell';
  before: string | undefined;
  after: string | undefined;
};

export type TableIdDiffResult = {
  changed: IdDiffEntry[];
  added: IdDiffEntry[];
  removed: IdDiffEntry[];
  isStable: boolean;
};

// ---------------------------------------------------------------------------
// Window augmentation
// ---------------------------------------------------------------------------

type TableIdDebugApi = {
  capture: (label?: string) => TableIdSnapshot | null;
  compare: () => TableIdDiffResult | null;
  compareWithInitial: () => TableIdDiffResult | null;
  history: () => Array<{ label: string; timestamp: number; tableCount: number }>;
};

declare global {
  interface Window {
    __TABLE_ID_DEBUG__?: TableIdDebugApi;
    __TABLE_ID_DEBUG_LOADING__?: boolean;
    __TABLE_ID_SNAPSHOTS__?: TableIdSnapshot[];
    __TABLE_ID_INITIAL_SNAPSHOT__?: TableIdSnapshot;
    __TABLE_ID_PREV_SNAPSHOT__?: TableIdSnapshot;
    __BLOCKNOTE_EDITOR__?: { document: unknown[] };
  }
}

// ---------------------------------------------------------------------------
// Core: スナップショット取得
// ---------------------------------------------------------------------------

/**
 * ブロック配列からテーブルの rowId / cellId をスナップショットとして抽出する。
 * tableContent 形式と tableRow[] 形式の両方に対応。
 */
export function captureTableIdSnapshot(
  blocks: unknown[],
  label: string,
): TableIdSnapshot {
  const tables: TableSnapshot[] = [];

  for (let blockIdx = 0; blockIdx < blocks.length; blockIdx++) {
    const block = blocks[blockIdx] as Record<string, unknown> | null;
    if (!block || typeof block !== 'object') continue;
    if (block.type !== 'table') continue;

    const rows = extractRows(block, blockIdx);
    if (rows.length > 0) {
      tables.push({
        blockId: typeof block.id === 'string' ? block.id : undefined,
        blockIndex: blockIdx,
        rows,
      });
    }
  }

  return { label, timestamp: Date.now(), tables };
}

function extractRows(block: Record<string, unknown>, blockIdx: number): RowIdEntry[] {
  // Pattern 1: content.type === 'tableContent' (legacy)
  const content = block.content as Record<string, unknown> | undefined;
  if (content && typeof content === 'object' && (content as { type?: string }).type === 'tableContent') {
    const contentRows = (content as { rows?: unknown[] }).rows;
    if (Array.isArray(contentRows)) {
      return contentRows.map((row, rowIdx) => rowToEntry(row, blockIdx, rowIdx));
    }
  }

  // Pattern 2: content is tableRow[] array
  if (Array.isArray(block.content)) {
    const arr = block.content as Array<Record<string, unknown>>;
    if (arr.some((item) => item && typeof item === 'object' && (item as { type?: string }).type === 'tableRow')) {
      return arr.map((row, rowIdx) => rowToEntry(row, blockIdx, rowIdx));
    }
  }

  // Pattern 3: props.rows (BlockNote simple table)
  const propsRows = (block.props as Record<string, unknown> | undefined)?.rows;
  if (Array.isArray(propsRows)) {
    return propsRows.map((row, rowIdx) => rowToEntry(row, blockIdx, rowIdx));
  }

  return [];
}

function rowToEntry(row: unknown, blockIdx: number, rowIdx: number): RowIdEntry {
  const rowObj = row as Record<string, unknown> | null;
  const rowId = rowObj && typeof rowObj === 'object'
    ? (typeof rowObj.rowId === 'string' ? rowObj.rowId : undefined)
    : undefined;

  const cells = extractCells(rowObj, blockIdx, rowIdx);

  return {
    position: `${blockIdx}:${rowIdx}`,
    rowId,
    cells,
  };
}

function extractCells(row: unknown, blockIdx: number, rowIdx: number): CellIdEntry[] {
  if (!row || typeof row !== 'object') return [];
  const rowObj = row as Record<string, unknown>;

  // cells フィールドから取得
  const cellSource = Array.isArray(rowObj.cells) ? rowObj.cells : Array.isArray(rowObj.content) ? rowObj.content : null;
  if (!cellSource) return [];

  return cellSource.map((cell, cellIdx) => {
    const cellObj = cell as Record<string, unknown> | null;
    const cellId = cellObj && typeof cellObj === 'object' && typeof cellObj.cellId === 'string'
      ? cellObj.cellId
      : undefined;
    return {
      position: `${blockIdx}:${rowIdx}:${cellIdx}`,
      cellId,
    };
  });
}

// ---------------------------------------------------------------------------
// Core: スナップショット差分比較
// ---------------------------------------------------------------------------

/**
 * 2 つのスナップショットを位置ベースで比較し、ID の変化を検出する。
 */
export function diffTableIdSnapshots(
  before: TableIdSnapshot,
  after: TableIdSnapshot,
): TableIdDiffResult {
  const changed: IdDiffEntry[] = [];
  const added: IdDiffEntry[] = [];
  const removed: IdDiffEntry[] = [];

  // 位置キー → ID のマップを構築
  const beforeMap = buildIdMap(before);
  const afterMap = buildIdMap(after);

  for (const [pos, entry] of beforeMap) {
    const afterEntry = afterMap.get(pos);
    if (!afterEntry) {
      removed.push({ position: pos, kind: entry.kind, before: entry.id, after: undefined });
    } else if (entry.id !== afterEntry.id) {
      changed.push({ position: pos, kind: entry.kind, before: entry.id, after: afterEntry.id });
    }
  }

  for (const [pos, entry] of afterMap) {
    if (!beforeMap.has(pos)) {
      added.push({ position: pos, kind: entry.kind, before: undefined, after: entry.id });
    }
  }

  return {
    changed,
    added,
    removed,
    isStable: changed.length === 0 && added.length === 0 && removed.length === 0,
  };
}

type IdMapEntry = { kind: 'row' | 'cell'; id: string | undefined };

function buildIdMap(snapshot: TableIdSnapshot): Map<string, IdMapEntry> {
  const map = new Map<string, IdMapEntry>();
  for (const table of snapshot.tables) {
    for (const row of table.rows) {
      map.set(`row:${row.position}`, { kind: 'row', id: row.rowId });
      for (const cell of row.cells) {
        map.set(`cell:${cell.position}`, { kind: 'cell', id: cell.cellId });
      }
    }
  }
  return map;
}

// ---------------------------------------------------------------------------
// Logging
// ---------------------------------------------------------------------------

/**
 * 差分をコンソールに出力する。ID 変化があれば warn、なければ info。
 */
export function logTableIdDiff(before: TableIdSnapshot, after: TableIdSnapshot): void {
  const diff = diffTableIdSnapshots(before, after);

  if (diff.isStable) {
    logger.info('[table-id-tracker] IDs stable', {
      before: before.label,
      after: after.label,
    });
    return;
  }

  if (diff.changed.length > 0) {
    logger.warn('[table-id-tracker] ID changes detected', {
      before: before.label,
      after: after.label,
      changed: diff.changed,
    });
  }

  if (diff.added.length > 0) {
    logger.info('[table-id-tracker] new IDs', {
      before: before.label,
      after: after.label,
      added: diff.added,
    });
  }

  if (diff.removed.length > 0) {
    logger.warn('[table-id-tracker] removed IDs', {
      before: before.label,
      after: after.label,
      removed: diff.removed,
    });
  }
}

// ---------------------------------------------------------------------------
// Console Debugger
// ---------------------------------------------------------------------------

/**
 * window.__TABLE_ID_DEBUG__ にデバッグ API を登録する。
 * dev/sandbox のみで呼び出す。
 */
export function installConsoleDebugger(): void {
  if (typeof window === 'undefined') return;
  if (window.__TABLE_ID_DEBUG__) return;

  let snapshots: ReadonlyArray<TableIdSnapshot> = [];
  const updateSnapshots = (next: ReadonlyArray<TableIdSnapshot>): void => {
    snapshots = next;
    window.__TABLE_ID_SNAPSHOTS__ = next as TableIdSnapshot[];
  };
  updateSnapshots([]);

  const api: TableIdDebugApi = {
    capture(label?: string) {
      const editor = window.__BLOCKNOTE_EDITOR__;
      if (!editor || !Array.isArray(editor.document)) {
        logger.warn('[table-id-tracker] No editor found');
        return null;
      }
      const snapshot = captureTableIdSnapshot(editor.document, label ?? `manual-${Date.now()}`);
      updateSnapshots([...snapshots, snapshot]);
      logger.info('[table-id-tracker] captured', {
        label: snapshot.label,
        tables: snapshot.tables.length,
      });
      return snapshot;
    },

    compare() {
      if (snapshots.length < 2) {
        logger.warn('[table-id-tracker] Need at least 2 snapshots to compare');
        return null;
      }
      const before = snapshots[snapshots.length - 2];
      const after = snapshots[snapshots.length - 1];
      logTableIdDiff(before, after);
      return diffTableIdSnapshots(before, after);
    },

    compareWithInitial() {
      const initial = window.__TABLE_ID_INITIAL_SNAPSHOT__;
      if (!initial) {
        logger.warn('[table-id-tracker] No initial snapshot found');
        return null;
      }
      const current = api.capture('compare-with-initial');
      if (!current) return null;
      logTableIdDiff(initial, current);
      return diffTableIdSnapshots(initial, current);
    },

    history() {
      return snapshots.map((s) => ({
        label: s.label,
        timestamp: s.timestamp,
        tableCount: s.tables.length,
      }));
    },
  };

  window.__TABLE_ID_DEBUG__ = api;
  logger.info('[table-id-tracker] console debugger installed – use __TABLE_ID_DEBUG__');
}
